import React, { useEffect, useState, useRef } from 'react';
import { boxShadows, colors } from '../../lib/styles';
import styled, { css } from 'styled-components';
import { useScrollPercentage } from '../../lib/useScrollPercentage';

const Outer = styled.div`
    width: 100%;
`;

const Inner = styled.div<{ hide?: boolean; sticky: boolean }>`
    position: relative;
    z-index: 6;
    display: flex;
    flex-direction: column;
    visibility: ${(props) => (props.hide ? `hidden` : `visible`)};
    opacity: ${(props) => (props.hide ? `0` : `1`)};
    transition: opacity 175ms;
    width: 100%;

    ${(props) =>
        props.sticky &&
        css`
            position: fixed;
            visibility: visible;
            opacity: 1;
            background: ${colors.white};
            box-shadow: ${boxShadows.medium};
            top: 0;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: opacity 175ms;
        `}
`;

interface Props {
    children: React.ReactNode;
    scrollProgressBar?: (progress: number) => React.ReactNode;
    hide?: boolean;
}

const StickyBar = ({ children, hide, scrollProgressBar }: Props) => {
    const [isSticky, setSticky] = useState(false);
    const outerRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    const [hasQueuedAnimation, setQueuedAnimation] = useState(false);
    const scrollPercentage = useScrollPercentage();

    useEffect(() => {
        function handleScroll() {
            if (!hasQueuedAnimation) {
                window.requestAnimationFrame(() => {
                    if (innerRef.current && outerRef.current) {
                        const inner = innerRef.current;
                        const outer = outerRef.current;
                        const isOnTop = inner.getBoundingClientRect().top <= 0;

                        if (isOnTop && !isSticky) {
                            setSticky(true);
                        } else if (
                            window.pageYOffset <
                            outer.getBoundingClientRect().top + window.pageYOffset
                        ) {
                            setSticky(false);
                        }

                        setQueuedAnimation(false);
                    }
                });
                setQueuedAnimation(true);
            }
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isSticky, hasQueuedAnimation]);

    return (
        <Outer ref={outerRef}>
            <Inner hide={hide} sticky={isSticky} ref={innerRef}>
                {children}
                {scrollProgressBar && scrollProgressBar(scrollPercentage)}
            </Inner>
        </Outer>
    );
};

export default StickyBar;
